import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import SortCategoriesModal from '../SortCategoriesModal';

function CategoryManagementSection() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [newCategory, setNewCategory] = useState({ name: '', description: '', parent_id: '', featured: false });
  const [sortModalOpen, setSortModalOpen] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/admin/categories');
      const cats = response.data.categories || [];
      // Sort by order_index to show reordered categories correctly
      cats.sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
      setCategories(cats);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const startEditingCategory = (category) => {
    setEditingCategory(category.id);
    setEditValues({
      name: category.name,
      description: category.description || '',
      parent_id: category.parent_id || '',
      featured: category.featured || false
    });
  };

  const handleUpdateCategory = async (categoryId) => {
    setLoading(true);
    try {
      const updatePayload = {
        name: editValues.name,
        description: editValues.description,
        parent_id: editValues.parent_id || null,  // Convert empty string to null
        featured: editValues.featured || false
      };
      await axios.put(`/admin/categories/${categoryId}`, updatePayload);
      toast.success('✅ Category updated successfully');
      setEditingCategory(null);
      setEditValues({});
      fetchCategories();
    } catch (error) {
      console.error('Failed to update category:', error);
      toast.error('Failed to update category');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }
    
    setLoading(true);
    try {
      await axios.delete(`/admin/categories/${categoryId}`);
      toast.success('✅ Category deleted successfully');
      fetchCategories();
    } catch (error) {
      console.error('Failed to delete category:', error);
      toast.error('Failed to delete category');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.name) {
      toast.error('Please enter a category name');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/admin/categories', {
        name: newCategory.name,
        description: newCategory.description,
        parent_id: newCategory.parent_id || null,
        featured: newCategory.featured || false
      });
      toast.success('✅ Category added successfully');
      setNewCategory({ name: '', description: '', parent_id: '', featured: false });
      fetchCategories();
    } catch (error) {
      console.error('Failed to add category:', error);
      toast.error('Failed to add category');
    } finally {
      setLoading(false);
    }
  };

  // Build a category tree with unlimited nesting
  const buildCategoryTree = (parentId = null, level = 0) => {
    return categories
      .filter(cat => {
        // Handle null, undefined, and empty string as "no parent"
        const catParentId = cat.parent_id || null;
        const targetParentId = parentId || null;
        return catParentId === targetParentId;
      })
      .map(category => ({
        ...category,
        level,
        children: buildCategoryTree(category.id, level + 1)
      }));
  };

  // Recursive component to render categories
  const CategoryItem = ({ category, level }) => {
    const indent = level * 24; // 24px indent per level
    
    return (
      <div key={category.id} className="border-b border-gray-100">
        <div className="flex items-center justify-between py-3 hover:bg-gray-50" style={{ paddingLeft: `${indent + 16}px` }}>
          {editingCategory === category.id ? (
            <div className="flex-1 flex gap-2 items-center">
              <span className="text-gray-400">{level > 0 && '↳'}</span>
              <input
                type="text"
                value={editValues.name}
                onChange={(e) => setEditValues(prev => ({ ...prev, name: e.target.value }))}
                className="flex-1 px-2 py-1 border rounded text-sm"
                placeholder="Category Name"
              />
              <input
                type="text"
                value={editValues.description}
                onChange={(e) => setEditValues(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description"
                className="flex-1 px-2 py-1 border rounded text-sm"
              />
              <select
                value={editValues.parent_id}
                onChange={(e) => setEditValues(prev => ({ ...prev, parent_id: e.target.value }))}
                className="px-2 py-1 border rounded text-sm"
              >
                <option value="">No Parent</option>
                {categories
                  .filter(c => c.id !== category.id)
                  .map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
              </select>
              <Button size="sm" onClick={() => handleUpdateCategory(category.id)} className="bg-custom-orange hover:bg-custom-orange">Save</Button>
              <Button size="sm" variant="outline" onClick={() => setEditingCategory(null)}>Cancel</Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                {level > 0 && <span className="text-gray-400">↳</span>}
                <div>
                  <span className={`${level === 0 ? 'font-semibold text-base' : 'text-sm'}`}>{category.name}</span>
                  {category.description && <p className="text-xs text-gray-500">{category.description}</p>}
                </div>
                <Badge variant={level === 0 ? 'default' : 'outline'} className="ml-2 text-xs">
                  Level {level + 1}
                </Badge>
              </div>
              <div className="space-x-2">
                <Button size="sm" variant="outline" onClick={() => startEditingCategory(category)}>Edit</Button>
                <Button size="sm" variant="destructive" onClick={() => handleDeleteCategory(category.id)} className="bg-red-600">Delete</Button>
              </div>
            </>
          )}
        </div>
        {category.children && category.children.length > 0 && (
          <div>
            {category.children.map(child => (
              <CategoryItem key={child.id} category={child} level={child.level} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const categoryTree = buildCategoryTree();

  const handleReorderCategories = async (categoryOrders) => {
    setLoading(true);
    try {
      console.log('Sending category reorder request:', categoryOrders);
      console.log('Full payload:', JSON.stringify({ categories: categoryOrders }, null, 2));
      const response = await axios.put('/admin/categories/reorder', { categories: categoryOrders });
      console.log('Category reorder response:', response.data);
      toast.success('✅ Category order saved!');
      await fetchCategories();
    } catch (error) {
      console.error('Failed to save category order:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      toast.error(`Failed to save: ${error.response?.data?.detail || error.response?.statusText || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Category Management</h2>
        <p className="text-gray-600">Manage product categories with unlimited nested subcategories</p>
        <div className="flex justify-center mt-4">
          <Button 
            onClick={() => setSortModalOpen(true)}
            className="bg-purple-600 hover:bg-purple-700"
          >
            🔄 Sort Categories
          </Button>
        </div>
      </div>

      {/* Add New Category */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Category</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <input
            type="text"
            placeholder="Category Name"
            value={newCategory.name}
            onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md"
          />
          <input
            type="text"
            placeholder="Description (optional)"
            value={newCategory.description}
            onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md"
          />
          <select
            value={newCategory.parent_id}
            onChange={(e) => setNewCategory(prev => ({ ...prev, parent_id: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="">No Parent (Top Level)</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          <Button 
            onClick={handleAddCategory}
            disabled={loading}
            className="bg-custom-orange hover:bg-custom-orange"
          >
            {loading ? 'Adding...' : 'Add Category'}
          </Button>
        </div>
      </Card>

      {/* Categories Tree */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Categories ({categories.length})</h3>
        <div className="space-y-1">
          {categoryTree.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No categories yet</div>
          ) : (
            categoryTree.map(category => (
              <CategoryItem key={category.id} category={category} level={category.level} />
            ))
          )}
        </div>
      </Card>

      {/* Sort Categories Modal */}
      <SortCategoriesModal
        categories={categories}
        onReorder={handleReorderCategories}
        isOpen={sortModalOpen}
        onClose={() => setSortModalOpen(false)}
      />
    </div>
  );
}


export default CategoryManagementSection;
